import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, InputBase, ButtonBase, Link } from "@mui/material";
import logo from "../../assets/legacy grey.png";
import { base_url } from "../../Global";

const CreateSuperAdmin = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    secretKey: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!form.secretKey.trim()) {
      setError("Secret key is required.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(base_url + "/auth/create-super-admin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          password: form.password,
          secretKey: form.secretKey,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to create Super Admin.");

      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Success screen ─────────────────────────────────────────────────────────
  if (success) {
    return (
      <Box className="w-full relative px-[20px] pt-[50px] pb-[50px] h-[100vh] text-left text-[1.125rem] text-white font-gilroy bg-gray-400 shadow-[0px_4px_74px_rgba(0,_0,_0,_0.25)] overflow-hidden flex flex-col items-center justify-center box-border">
        <Box className="m-0 rounded-3xl bg-gray-200 flex flex-col items-center justify-center pt-[2rem] pb-[2rem] px-[3rem] gap-[1rem] z-[2] text-center">
          <Box style={{
            width: "64px", height: "64px", borderRadius: "50%",
            background: "#FF6B35", display: "flex",
            alignItems: "center", justifyContent: "center", fontSize: "28px",
          }}>
            ★
          </Box>
          <Typography component="h2" className="m-0 text-inherit text-[1.5rem] font-bold font-inherit capitalize">
            Super Admin Created!
          </Typography>
          <Typography className="text-[0.9rem] text-gray-300 font-gilroy text-center max-w-[300px]">
            The Super Admin account has been set up successfully. Redirecting to login…
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────
  return (
    <Box className="w-full relative px-[20px] pt-[30px] pb-[30px] min-h-[100vh] text-left text-[1.125rem] text-white font-gilroy bg-gray-400 shadow-[0px_4px_74px_rgba(0,_0,_0,_0.25)] overflow-auto flex flex-col items-center justify-start box-border gap-[1rem] tracking-[normal]">
      <Box
        component="form"
        onSubmit={handleSubmit}
        className="m-0 rounded-3xl bg-gray-200 flex flex-col items-center justify-start mt-6 pt-[1.75rem] pb-[2rem] pr-[2.94rem] pl-[3rem] box-border gap-[0.85rem] w-full max-w-[480px] z-[2] mq700:pl-[1.5rem] mq700:pr-[1.44rem] mq700:box-border"
      >
        {/* Logo + heading */}
        <Box className="self-stretch flex flex-col items-center justify-start gap-[0.5rem]">
          <Box
            component="img"
            className="w-[150px] h-[36px] relative z-[3]"
            loading="eager"
            alt="Logo"
            src={logo}
          />
          <Typography
            component="h1"
            className="m-0 flex-1 relative text-inherit leading-[2.375rem] capitalize font-bold font-inherit flex items-center z-[1]"
          >
            Create Super Admin
          </Typography>
          <Typography className="text-[0.85rem] text-gray-300 font-gilroy text-center -mt-2">
            One-time initial setup · Requires a secret key
          </Typography>
        </Box>

        {/* Warning banner */}
        <Box style={{
          width: "100%", borderRadius: "8px",
          padding: "10px 14px",
          background: "rgba(255,107,53,0.12)",
          border: "1px solid rgba(255,107,53,0.35)",
        }}>
          <Typography style={{ color: "#FF6B35", fontSize: "12px", fontFamily: "Gilroy, sans-serif" }}>
            This page should only be used once during initial system setup.
            After a Super Admin exists, this route will be disabled by your backend.
          </Typography>
        </Box>

        {/* Full Name */}
        <Field label="Full Name">
          <InputBase
            value={form.name}
            onChange={handleChange("name")}
            required
            placeholder="Super Admin name"
            className="w-full bg-gray-500 h-[48px] rounded-3xs py-[13px] px-[18px] font-gilroy font-light text-[1rem] text-gray-400 min-w-[300px] z-[1]"
          />
        </Field>

        {/* Email */}
        <Field label="Email Address">
          <InputBase
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            required
            placeholder="superadmin@example.com"
            className="w-full bg-gray-500 h-[48px] rounded-3xs py-[13px] px-[18px] font-gilroy font-light text-[1rem] text-gray-400 min-w-[300px] z-[1]"
          />
        </Field>

        {/* Password */}
        <Field label="Password">
          <InputBase
            type="password"
            value={form.password}
            onChange={handleChange("password")}
            required
            placeholder="Min. 8 characters"
            className="w-full bg-gray-500 h-[48px] rounded-3xs py-[13px] px-[18px] font-gilroy font-light text-[1rem] text-gray-800 min-w-[300px] z-[1]"
          />
        </Field>

        {/* Confirm Password */}
        <Field label="Confirm Password">
          <InputBase
            type="password"
            value={form.confirmPassword}
            onChange={handleChange("confirmPassword")}
            required
            placeholder="Repeat your password"
            className="w-full bg-gray-500 h-[48px] rounded-3xs py-[13px] px-[18px] font-gilroy font-light text-[1rem] text-gray-800 min-w-[300px] z-[1]"
          />
        </Field>

        {/* Secret Key */}
        <Field label="Secret Key">
          <InputBase
            type="password"
            value={form.secretKey}
            onChange={handleChange("secretKey")}
            required
            placeholder="Server-side secret key"
            className="w-full bg-gray-500 h-[48px] rounded-3xs py-[13px] px-[18px] font-gilroy font-light text-[1rem] text-gray-400 min-w-[300px] z-[1]"
          />
        </Field>

        {/* Secret key hint */}
        <Box style={{
          width: "100%", borderRadius: "8px", padding: "8px 14px",
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}>
          <Typography style={{ color: "#6b7280", fontSize: "11px", fontFamily: "Gilroy, sans-serif" }}>
            The secret key is set in your backend environment variables (e.g.{" "}
            <code style={{ color: "#9ca3af" }}>SUPER_ADMIN_SECRET</code>).
            Ask your backend developer if you don't have it.
          </Typography>
        </Box>

        {/* Error */}
        {error && (
          <Box style={{
            width: "100%", borderRadius: "8px",
            background: "rgba(239,68,68,0.12)",
            border: "1px solid rgba(239,68,68,0.3)",
            padding: "10px 14px",
          }}>
            <Typography style={{ color: "#f87171", fontSize: "13px", fontFamily: "Gilroy, sans-serif" }}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Submit */}
        <Box className="self-stretch flex flex-col items-start justify-start max-w-full mt-1">
          <ButtonBase
            type="submit"
            disabled={loading}
            className="no-underline cursor-pointer bg-white self-stretch rounded-lg flex flex-row items-center justify-center box-border max-w-full whitespace-nowrap z-[3] hover:bg-gray-100"
            style={{ height: "48px", opacity: loading ? 0.7 : 1 }}
          >
            <Box className="relative text-[18px] leading-[40px] text-gray-200 capitalize font-semibold font-poppins text-left z-[4]">
              {loading ? "Creating..." : "Create Super Admin"}
            </Box>
          </ButtonBase>

          <Box className="self-stretch flex flex-col items-center justify-start mt-3 z-[3]">
            <Link
              component="button"
              type="button"
              onClick={() => navigate("/login")}
              className="text-white text-[0.9rem] font-gilroy underline cursor-pointer"
            >
              Back to Login
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const Field = ({ label, children }) => (
  <Box className="self-stretch flex flex-col items-start justify-start gap-[0.25rem] w-full">
    <Box className="relative leading-[18px] capitalize flex items-center shrink-0 z-[1] text-[0.9rem]">
      {label}
    </Box>
    {children}
  </Box>
);

export default CreateSuperAdmin;
